/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import { Injectable } from '@angular/core';
import { AngularFireDatabase } from '@angular/fire/compat/database';

import { FancyActionTrackerFactory } from './fancy-action-tracker-factory.service';

@Injectable({
	providedIn: 'root',
})
export abstract class FancyFirebaseSplitDataListFactory {
	public extraAngulars; /** to be optionally populated by inheritors so they can customize their AngularLandConnection. Expects an object */
	constructor(
		protected db: AngularFireDatabase,
		protected fancyActionTrackerFactory: FancyActionTrackerFactory
	) {}

	public create(ListConstructor, path: RefOrObservable, fancyJoins?, additionalDependencies: any[] = []) {
		return new ListConstructor(
			{
				angularFireDatabase: this.db,
				fancyActionTrackerFactory: this.fancyActionTrackerFactory,
				...this.extraAngulars,
			},
			path,
			fancyJoins,
			...additionalDependencies
		);
	}
}

//Shortest possible syntax, steps:
//- import List
//- export class file
//- change two List refs, and one ListFactory ref

// RoleList
// -----------------------------------------------------------------------------
import { RoleList } from '../lists/RoleList';
export * from '../lists/RoleList';

@Injectable({
	providedIn: 'root',
})
export class RoleListFactory extends FancyFirebaseSplitDataListFactory {
	constructor(
		protected db: AngularFireDatabase,
		protected fancyActionTrackerFactory: FancyActionTrackerFactory
	) {
		super(db, fancyActionTrackerFactory);
	}
	public create(path: RefOrObservable, fancyJoins?): RoleList {
		return super.create(RoleList, path, fancyJoins);
	}
}

// InvitedUserList
// -----------------------------------------------------------------------------
import { InvitedUserList } from '../lists/InvitedUserList';
export * from '../lists/InvitedUserList';

@Injectable({
	providedIn: 'root',
})
export class InvitedUserListFactory extends FancyFirebaseSplitDataListFactory {
	constructor(
		protected db: AngularFireDatabase,
		protected fancyActionTrackerFactory: FancyActionTrackerFactory
	) {
		super(db, fancyActionTrackerFactory);
	}
	public create(path: RefOrObservable, fancyJoins?): InvitedUserList {
		return super.create(InvitedUserList, path, fancyJoins);
	}
}

// ColorList
// -----------------------------------------------------------------------------
import { ColorList } from '../lists/ColorList';
export * from '../lists/ColorList';

@Injectable({
	providedIn: 'root',
})
export class ColorListFactory extends FancyFirebaseSplitDataListFactory {
	constructor(
		protected db: AngularFireDatabase,
		protected fancyActionTrackerFactory: FancyActionTrackerFactory
	) {
		super(db, fancyActionTrackerFactory);
	}

	public create(path: RefOrObservable, fancyJoins?): ColorList {
		return super.create(ColorList, path, fancyJoins);
	}
}

// LocationList
// -----------------------------------------------------------------------------
import { LocationList } from '../lists/LocationList';
export * from '../lists/LocationList';

@Injectable({
	providedIn: 'root',
})
export class LocationListFactory extends FancyFirebaseSplitDataListFactory {
	constructor(
		protected db: AngularFireDatabase,
		protected fancyActionTrackerFactory: FancyActionTrackerFactory
	) {
		super(db, fancyActionTrackerFactory);
	}
	public create(path: RefOrObservable, fancyJoins?): LocationList {
		return super.create(LocationList, path, fancyJoins);
	}
}

// ProjectMemberList
// -----------------------------------------------------------------------------
import { ProjectMemberList } from '../lists/ProjectMemberList';
export * from '../lists/ProjectMemberList';

@Injectable({
	providedIn: 'root',
})
export class ProjectMemberListFactory extends FancyFirebaseSplitDataListFactory {
	constructor(
		protected db: AngularFireDatabase,
		protected fancyActionTrackerFactory: FancyActionTrackerFactory
	) {
		super(db, fancyActionTrackerFactory);
	}
	public create(path: RefOrObservable, fancyJoins?): ProjectMemberList {
		return super.create(ProjectMemberList, path, fancyJoins);
	}
}

// PriorityList
// -----------------------------------------------------------------------------
import { PriorityList } from '../lists/PriorityList';
export * from '../lists/PriorityList';

@Injectable({
	providedIn: 'root',
})
export class PriorityListFactory extends FancyFirebaseSplitDataListFactory {
	constructor(
		protected db: AngularFireDatabase,
		protected fancyActionTrackerFactory: FancyActionTrackerFactory
	) {
		super(db, fancyActionTrackerFactory);
	}
	public create(path: RefOrObservable, fancyJoins?): PriorityList {
		return super.create(PriorityList, path, fancyJoins);
	}
}

// VarianceList
// -----------------------------------------------------------------------------
import { VarianceList } from '../lists/VarianceList';
export * from '../lists/VarianceList';

@Injectable({
	providedIn: 'root',
})
export class VarianceListFactory extends FancyFirebaseSplitDataListFactory {
	constructor(
		protected db: AngularFireDatabase,
		protected fancyActionTrackerFactory: FancyActionTrackerFactory
	) {
		super(db, fancyActionTrackerFactory);
	}
	public create(path: RefOrObservable, fancyJoins?): VarianceList {
		return super.create(VarianceList, path, fancyJoins);
	}
}

// PlanList
// -----------------------------------------------------------------------------
import { PlanList } from '../lists/PlanList';
export * from '../lists/PlanList';

@Injectable({
	providedIn: 'root',
})
export class PlanListFactory extends FancyFirebaseSplitDataListFactory {
	constructor(
		protected db: AngularFireDatabase,
		protected fancyActionTrackerFactory: FancyActionTrackerFactory
	) {
		super(db, fancyActionTrackerFactory);
	}
	public create(path: RefOrObservable, fancyJoins?): PlanList {
		return super.create(PlanList, path, fancyJoins);
	}
}

// FancyListIndexDump
// -----------------------------------------------------------------------------
import { FancyListIndexDump } from '../lists/FancyListIndexDump';
export * from '../lists/FancyListIndexDump';

@Injectable({
	providedIn: 'root',
})
export class FancyListIndexDumpFactory extends FancyFirebaseSplitDataListFactory {
	constructor(
		protected db: AngularFireDatabase,
		protected fancyActionTrackerFactory: FancyActionTrackerFactory
	) {
		super(db, fancyActionTrackerFactory);
	}
	public create(path: RefOrObservable, fancyJoins?): FancyListIndexDump {
		return super.create(FancyListIndexDump, path, fancyJoins);
	}
}

// LaborCountList
// -----------------------------------------------------------------------------
import { LaborCountList } from '../lists/LaborCountList';
export * from '../lists/LaborCountList';

@Injectable({
	providedIn: 'root',
})
export class LaborCountListFactory extends FancyFirebaseSplitDataListFactory {
	constructor(
		protected db: AngularFireDatabase,
		protected fancyActionTrackerFactory: FancyActionTrackerFactory
	) {
		super(db, fancyActionTrackerFactory);
	}
	public create(path: RefOrObservable, fancyJoins?): LaborCountList {
		return super.create(LaborCountList, path, fancyJoins);
	}
}

// TicketExceptionDaysList
// -----------------------------------------------------------------------------
import { TicketExceptionDaysList } from '../lists/TicketExceptionDaysList';
export * from '../lists/TicketExceptionDaysList';

@Injectable({
	providedIn: 'root',
})
export class TicketExceptionDaysListFactory extends FancyFirebaseSplitDataListFactory {
	constructor(
		protected db: AngularFireDatabase,
		protected fancyActionTrackerFactory: FancyActionTrackerFactory
	) {
		super(db, fancyActionTrackerFactory);
	}
	public create(path: RefOrObservable, fancyJoins?): TicketExceptionDaysList {
		return super.create(TicketExceptionDaysList, path, fancyJoins);
	}
}

// ProjectList
// -----------------------------------------------------------------------------
import { ProjectList } from '../lists/ProjectList';
export * from '../lists/ProjectList';

@Injectable({
	providedIn: 'root',
})
export class ProjectListFactory extends FancyFirebaseSplitDataListFactory {
	constructor(
		protected db: AngularFireDatabase,
		protected fancyActionTrackerFactory: FancyActionTrackerFactory
	) {
		super(db, fancyActionTrackerFactory);
	}
	public create(path: RefOrObservable, fancyJoins?): ProjectList {
		return super.create(ProjectList, path, fancyJoins);
	}
}

// PlanAccessList
// -----------------------------------------------------------------------------
import { PlanAccessList } from '../lists/PlanAccessList';
import { RefOrObservable } from '../base/interfaces';
export * from '../lists/PlanAccessList';

@Injectable({
	providedIn: 'root',
})
export class PlanAccessListFactory extends FancyFirebaseSplitDataListFactory {
	constructor(
		protected db: AngularFireDatabase,
		protected fancyActionTrackerFactory: FancyActionTrackerFactory
	) {
		super(db, fancyActionTrackerFactory);
	}
	public create(path: RefOrObservable, fancyJoins?): PlanAccessList {
		return super.create(PlanAccessList, path, fancyJoins);
	}
}

/*
 * generic list creation
 * DO NOT create new ListFactory here! try to reuse this generic one if possible
 * Search for how this is used in Custom Fields as an example
 */
@Injectable({
	providedIn: 'root',
})
export class FirebaseListFactory extends FancyFirebaseSplitDataListFactory {
	constructor(
		protected db: AngularFireDatabase,
		protected fancyActionTrackerFactory: FancyActionTrackerFactory
	) {
		super(db, fancyActionTrackerFactory);
	}

	/**
	 * @param type a reference to a constructor
	 * @param path
	 * @param fancyJoins
	 */
	public create<T>(type: new (...args) => T, path: RefOrObservable, fancyJoins?): T {
		return super.create(type, path, fancyJoins);
	}
}
